/**
 * Funkcje pomocnicze do wyznaczania klasy pojazdu w dokumencie Certificate of Conformity.
 * Klasa zależy od łącznej liczby pasażerów (miejsca siedzące + stojące) oraz od tego,
 * czy zabudowa to City Line (pojazd miejski), czy inna (np. Tourist Line - międzymiastowy).
 *
 * @module vehicle-class-utils
 */

/** Próg liczby pasażerów rozdzielający klasy I/II/III (powyżej) od A/B (do progu włącznie) */
const PASSENGER_CLASS_THRESHOLD = 22;

/** Nazwa zabudowy oznaczająca pojazd miejski */
const CITY_LINE_CONVERSION_NAME = "City Line";

/** Klasa pojazdu w notacji rzymskiej, zgodna z polem {{vehicleClass}} */
export type VehicleClassRoman = "I" | "II" | "III" | "A" | "B";

/** Klasa pojazdu w notacji uproszczonej (arabskiej), zgodna z polem {{vehicleClassSimplified}} */
export type VehicleClassSimplified = "1" | "2" | "3" | "A" | "B";

/** Wynik wyznaczenia klasy pojazdu - obie notacje jednocześnie */
export interface VehicleClassResult {
  vehicleClass: VehicleClassRoman;
  vehicleClassSimplified: VehicleClassSimplified;
}

/**
 * Sprawdza, czy dana zabudowa to City Line (pojazd miejski).
 *
 * @param conversionName - Nazwa zabudowy (np. "City Line", "Tourist Line")
 * @returns true, jeśli zabudowa to City Line
 */
export function isCityLineConversion(
  conversionName: string | undefined | null,
): boolean {
  return conversionName?.trim() === CITY_LINE_CONVERSION_NAME;
}

/**
 * Wyznacza klasę pojazdu (w obu notacjach) na podstawie łącznej liczby pasażerów
 * i rodzaju zabudowy.
 *
 * Zasady:
 * - Powyżej 22 pasażerów (siedzących + stojących):
 *   - brak miejsc stojących (tylko siedzące) → klasa III / 3
 *   - City Line (miejsca stojące i siedzące) → klasa I / 1
 *   - pozostałe zabudowy, np. międzymiastowe Tourist Line → klasa II / 2
 * - Do 22 pasażerów włącznie (siedzących i stojących razem):
 *   - City Line → klasa A
 *   - pozostałe zabudowy, np. Tourist Line → klasa B
 *
 * @param sittingPlacesCount - Liczba miejsc siedzących
 * @param standingPlacesCount - Liczba miejsc stojących
 * @param conversionName - Nazwa zabudowy (do rozpoznania City Line)
 * @returns Wyznaczona klasa pojazdu w obu notacjach
 */
export function resolveVehicleClass(
  sittingPlacesCount: number,
  standingPlacesCount: number,
  conversionName: string | undefined | null,
): VehicleClassResult {
  const totalPassengers =
    (sittingPlacesCount || 0) + (standingPlacesCount || 0);
  const isCityLine = isCityLineConversion(conversionName);

  if (totalPassengers > PASSENGER_CLASS_THRESHOLD) {
    if (!standingPlacesCount) {
      return { vehicleClass: "III", vehicleClassSimplified: "3" };
    }
    if (isCityLine) {
      return { vehicleClass: "I", vehicleClassSimplified: "1" };
    }
    return { vehicleClass: "II", vehicleClassSimplified: "2" };
  }

  if (isCityLine) {
    return { vehicleClass: "A", vehicleClassSimplified: "A" };
  }
  return { vehicleClass: "B", vehicleClassSimplified: "B" };
}
